import React, { useEffect } from 'react';

import { useEnableClientCommonStepUpAuth } from '../../../feature-flags/hooks/use-enable-client-common-step-up-auth';
import { UpdateMfaClaimContextProvider as LegacyUpdateMfaClaimContextProvider } from '../update-mfa-claim-context';
import { bridgeLegacyMfaRequiredSubject } from './bridge-legacy-mfa-subject';
import { UpdateMfaClaimContextProvider as ClientCommonUpdateMfaClaimContextProvider } from './update-mfa-claim-provider';

/**
 * Selects between the legacy step-up auth provider and the client-common
 * adapter based on `enable-client-common-step-up-auth`.
 *
 * Both providers own the same responsibility — queue requests that failed with
 * an MFA-required 403, drive the modal, replay the queue — so exactly one may be
 * mounted at a time or a single failure would raise two modals.
 *
 * Feature flags resolve asynchronously from Redux, so this starts on the legacy
 * provider and swaps once the flag arrives. That window closes long before a
 * user can trigger an MFA-required request, and whichever provider is mounted at
 * the time handles it end to end.
 *
 * Delete this switch and mount the adapter directly once the flag reaches 100%.
 */
export const UpdateMfaClaimProviderSwitch = ({ children }: { children: React.ReactNode }) => {
  const isClientCommonStepUpAuthEnabled = useEnableClientCommonStepUpAuth();

  useEffect(() => {
    if (!isClientCommonStepUpAuthEnabled) {
      return undefined;
    }

    return bridgeLegacyMfaRequiredSubject();
  }, [isClientCommonStepUpAuthEnabled]);

  if (isClientCommonStepUpAuthEnabled) {
    return <ClientCommonUpdateMfaClaimContextProvider>{children}</ClientCommonUpdateMfaClaimContextProvider>;
  }

  return <LegacyUpdateMfaClaimContextProvider>{children}</LegacyUpdateMfaClaimContextProvider>;
};
